import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { Section } from '../components/ui/Section';
import { MapPin, Globe, Wifi, Navigation, Clock, Building2, Loader2 } from 'lucide-react';
interface BrowserCoords {
  latitude: number;
  longitude: number;
  accuracy: number;
}
function DataRow({
  label,
  value,
  icon
}: {
  label: string;
  value: string | undefined;
  icon: React.ReactNode;
}) {
  return <div className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0" data-id="element-729">
      <div className="w-10 h-10 rounded-lg bg-[#00316B]/5 flex items-center justify-center text-[#00316B] shrink-0" data-id="element-730">
        {icon}
      </div>
      <div className="flex-1 min-w-0" data-id="element-731">
        <p className="text-sm text-gray-500 mb-0.5" data-id="element-732">{label}</p>
        <p className="text-base font-medium text-[#0C0C0C] font-mono break-all" data-id="element-733">
          {value || <span className="text-gray-300 italic font-sans" data-id="element-734">
              Not available
            </span>}
        </p>
      </div>
    </div>;
}
export function GeoDebugPage() {
  const {
    geo,
    loading
  } = useGeoLocation();
  const [browserCoords, setBrowserCoords] = useState<BrowserCoords | null>(null);
  const [browserGeoStatus, setBrowserGeoStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable'>('idle');
  const requestBrowserLocation = () => {
    if (!navigator.geolocation) {
      setBrowserGeoStatus('unavailable');
      return;
    }
    setBrowserGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(position => {
      setBrowserCoords({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      });
      setBrowserGeoStatus('granted');
    }, () => {
      setBrowserGeoStatus('denied');
    }, {
      enableHighAccuracy: true,
      timeout: 10000
    });
  };
  return <main className="pt-24 pb-12" data-id="element-735">
      <Section background="default" className="!py-10 md:!py-14" data-id="element-736">
        <div className="max-w-2xl" data-id="element-737">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-738">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium mb-4" data-id="element-739">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" data-id="element-740" />
              Internal Dev Tool
            </div>
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-741">
              Geo Data Test
            </h1>
            <p className="text-gray-600" data-id="element-742">
              Testing location data collection from IP lookup and browser
              geolocation APIs before building the product recommendation page.
            </p>
          </motion.div>
        </div>
      </Section>

      <Section background="white" className="!py-10 md:!py-14" data-id="element-743">
        <div className="max-w-2xl space-y-8" data-id="element-744">
          {/* IP-based Geo Data */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-745">
            <h2 className="text-lg font-bold font-heading text-[#0C0C0C] mb-1" data-id="element-746">
              IP-based Location
            </h2>
            <p className="text-sm text-gray-500 mb-4" data-id="element-747">
              Data from ipapi.co — no user permission required
            </p>

            {loading ? <div className="flex items-center gap-3 py-8 text-gray-400" data-id="element-748">
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-749" />
                Fetching IP location data…
              </div> : geo ? <div className="bg-white rounded-xl border border-gray-200 px-5" data-id="element-750">
                <DataRow label="IP Address" value={geo.ip} icon={<Wifi className="w-4 h-4" data-id="element-752" />} data-id="element-751" />
                <DataRow label="Country" value={geo.country ? `${geo.flag} ${geo.country} (${geo.countryCode})` : undefined} icon={<Globe className="w-4 h-4" data-id="element-754" />} data-id="element-753" />
                <DataRow label="City" value={geo.city} icon={<MapPin className="w-4 h-4" data-id="element-756" />} data-id="element-755" />
                <DataRow label="Region" value={geo.region} icon={<MapPin className="w-4 h-4" data-id="element-758" />} data-id="element-757" />
                <DataRow label="Coordinates (approx)" value={geo.latitude && geo.longitude ? `${geo.latitude}, ${geo.longitude}` : undefined} icon={<Navigation className="w-4 h-4" data-id="element-760" />} data-id="element-759" />
                <DataRow label="Timezone" value={geo.timezone} icon={<Clock className="w-4 h-4" data-id="element-762" />} data-id="element-761" />
                <DataRow label="ISP / Org" value={geo.org} icon={<Building2 className="w-4 h-4" data-id="element-764" />} data-id="element-763" />
              </div> : <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700 text-sm" data-id="element-765">
                Failed to fetch IP location data. The API may be rate-limited or
                blocked.
              </div>}
          </motion.div>

          {/* Browser GPS */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-766">
            <h2 className="text-lg font-bold font-heading text-[#0C0C0C] mb-1" data-id="element-767">
              Browser GPS
            </h2>
            <p className="text-sm text-gray-500 mb-4" data-id="element-768">
              Precise coordinates via browser Geolocation API — requires user
              permission
            </p>

            {browserGeoStatus === 'idle' && <button onClick={requestBrowserLocation} className="px-5 py-2.5 bg-[#00316B] text-white rounded-lg text-sm font-medium hover:bg-[#002554] transition-colors" data-id="element-769">
                Request Browser Location
              </button>}

            {browserGeoStatus === 'requesting' && <div className="flex items-center gap-3 py-4 text-gray-500" data-id="element-770">
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-771" />
                Waiting for permission…
              </div>}

            {browserGeoStatus === 'granted' && browserCoords && <div className="bg-white rounded-xl border border-gray-200 px-5" data-id="element-772">
                <DataRow label="Latitude" value={String(browserCoords.latitude)} icon={<Navigation className="w-4 h-4" data-id="element-774" />} data-id="element-773" />
                <DataRow label="Longitude" value={String(browserCoords.longitude)} icon={<Navigation className="w-4 h-4" data-id="element-776" />} data-id="element-775" />
                <DataRow label="Accuracy" value={`±${Math.round(browserCoords.accuracy)}m`} icon={<MapPin className="w-4 h-4" data-id="element-778" />} data-id="element-777" />
              </div>}

            {browserGeoStatus === 'denied' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-800 text-sm" data-id="element-779">
                Location permission denied by user. Browser GPS coordinates are
                not available.
              </div>}

            {browserGeoStatus === 'unavailable' && <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-gray-600 text-sm" data-id="element-780">
                Geolocation API is not available in this browser.
              </div>}
          </motion.div>

          {/* Summary */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-[#00316B]/5 rounded-xl p-5 text-sm text-gray-600 space-y-2" data-id="element-781">
            <p className="font-semibold text-[#0C0C0C]" data-id="element-782">
              Available for recommendations:
            </p>
            <ul className="space-y-1 ml-4 list-disc" data-id="element-783">
              <li data-id="element-784">
                Country-level targeting:{' '}
                <span className="font-mono text-[#00316B]" data-id="element-785">
                  {geo?.country || '—'}
                </span>
              </li>
              <li data-id="element-786">
                City-level targeting:{' '}
                <span className="font-mono text-[#00316B]" data-id="element-787">
                  {geo?.city || '—'}
                </span>
              </li>
              <li data-id="element-788">
                Approximate coords:{' '}
                <span className="font-mono text-[#00316B]" data-id="element-789">
                  {geo?.latitude && geo?.longitude ? `${geo.latitude}, ${geo.longitude}` : '—'}
                </span>
              </li>
              <li data-id="element-790">
                Precise GPS:{' '}
                <span className="font-mono text-[#00316B]" data-id="element-791">
                  {browserCoords ? `${browserCoords.latitude}, ${browserCoords.longitude}` : 'Not requested / denied'}
                </span>
              </li>
            </ul>
          </motion.div>
        </div>
      </Section>
    </main>;
}